import React, {useState, useCallback, useRef, useMemo, useEffect} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {makeStyles} from '@mui/styles'
import {useSnackbar} from 'notistack';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {useToggle} from '../../../utils/hooks';
import {useTranslation} from 'react-i18next';
import SMCLabel from '../../../components/Simple/SMCLabel';
import {LinearProgress} from '@mui/material';
import Api from '../../../api/SMPFetchHelper';
import {getAxiosFilename, enqueueExcelDownload, getErrorCode} from '../../../utils/utils';
import SMCButton from '../../../components/Simple/SMCButton';
import downloadIcon from '../../../assets/images/download.svg';
import RowLayout from '../../../components/Layouts/RowLayout';
import {ObjectTypes} from '../../../api/consts';

const useStyles = makeStyles(theme => ({
    card: {
        maxWidth: 400,
        minWidth: 344,
    },
    header: {
        color: '#FFF',
        fontWeight: 'bold',
        fontSize: '0.875rem',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        lineHeight: '1.57',
        letterSpacing: '0.00714em'
    },
    actionRoot: {
        padding: '8px 8px 8px 16px',
        backgroundColor: '#2F3F57',
    },
    icons: {
        marginLeft: 'auto !important'
    },
    expand: {
        padding: '8px 8px',
        transform: 'rotate(0deg)',
        transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.shortest,
        }),
    },
    expandOpen: {
        transform: 'rotate(180deg)',
    },
    collapse: {
        padding: 16,
    },
    checkIcon: {
        fontSize: 20,
        color: '#b3b3b3',
        paddingRight: 4,
    },
    button: {
        padding: 0,
        textTransform: 'none',
    },
}));

const LogsToExcelSnackbarContent = React.forwardRef(({
    message,
    objectType,
    id,
    filterObject
}, ref) => {
    const { t } = useTranslation();
    const { closeSnackbar } = useSnackbar();
    const classes = useStyles();

    const [inProgress, setInProgress] = useState(true);
    const [stopped, setStopped] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [progressPercent, setProgressPercent] = useState(0);
    const [excelData, setExcelData] = useState(null);
    const [excelName, setExcelName] = useState(null);

    const canceled = useRef(false);

    const [expanded, toggleExpanded] = useToggle(false);

    const handleExpandClick = useCallback(() => {
        toggleExpanded();
    }, [toggleExpanded]);

    const handleDismiss = useCallback(() => {
        canceled.current = true;
        closeSnackbar(id);
    }, [closeSnackbar, id]);

    const startExport = useCallback(async (filter) => {
        try {
            let logsToExcelObjectType;
            if (objectType === ObjectTypes.ACTIVITY_LOGS) {
                logsToExcelObjectType = 'ACTIVITY';
            }
            else if (objectType === ObjectTypes.SYSTEM_LOGS) {
                logsToExcelObjectType = 'SYSTEM';
            }
            const resp = await Api.logsToExcelStart(logsToExcelObjectType, filter);
            const taskId = resp.data.data.taskID;
            const checkProgress = async () => {
                if (canceled.current === true) {
                    return;
                }
                try {
                    const resp = await Api.logsToExcelGetProgress(taskId);
                    const respData = resp.data.data;
                    if (respData.message === 'STATE_RUNNING') {
                        const percent = respData.currentNumber / (respData.max || 1);
                        setProgressPercent(percent * 100);
                        setTimeout(checkProgress, 100);
                    }
                    else if (respData.message === 'STATE_DONE') {
                        setInProgress(false);
                        const resp = await Api.logsToExcelDone(taskId);
                        const fileName = getAxiosFilename(resp) || 'excel.xlsx';
                        setExcelData(resp.data);
                        setExcelName(fileName);
                    }
                    else if (respData.message === 'STATE_STOPPED') {
                        setStopped(true);
                    }
                } catch (e) {
                    setHasError(true);
                    const errorCode = getErrorCode(e) || 'GENERAL_ERROR';
                    setErrorMessage(t(errorCode));
                }
            }
            checkProgress();
        } catch (e) {
            setHasError(true);
            const errorCode = getErrorCode(e) || 'GENERAL_ERROR';
            setErrorMessage(t(errorCode));
        }
    }, [objectType, t]);

    useEffect(() => {
        // initiate export with filter
        startExport(filterObject);
    }, [filterObject, startExport, handleDismiss]);

    const headerText = useMemo(() => {
        if (hasError) {
            return 'Error';
        }
        else if (inProgress) {
            return 'Creating excel file...';
        }
        else if (stopped) {
            return 'Progress cancelled...';
        }
        else {
            return 'Finished creating Excel file';
        }
    }, [hasError, inProgress, stopped]);

    const body = useMemo(() => {
        if (hasError) {
            return <SMCLabel>{errorMessage}. Please try again</SMCLabel>
        }
        else if (inProgress) {
            return (
                <LinearProgress style={{ width: '100%' }} variant="determinate" value={progressPercent} />
            )
        }
        else if (stopped) {
            return null;
        }
        else if (excelData && excelName) {
            return (
                <SMCButton tooltip={t('SMC_GEN_DOWNLOAD')} variant="ghost" onClick={() => enqueueExcelDownload(excelData, excelName)}>
                    <RowLayout spacing="4px">
                        <img src={downloadIcon} alt="download" width="16px" height="16px" />
                        <SMCLabel bold inline>Click to download</SMCLabel>
                    </RowLayout>
                </SMCButton>
            );
        }
        else {
            return null;
        }
    }, [hasError, errorMessage, inProgress, stopped, progressPercent, excelData, excelName, t]);
    
    return (
        <Card className={classes.card} ref={ref}>
            <CardActions classes={{ root: classes.actionRoot }}>
                <SMCLabel className={classes.header}>{headerText}</SMCLabel>
                <div className={classes.icons}>
                    <IconButton
                        aria-label="Show more"
                        className={classnames(classes.expand, { [classes.expandOpen]: expanded })}
                        onClick={handleExpandClick}
                    >
                        <ExpandMoreIcon style={{ color: 'white' }} />
                    </IconButton>
                    <IconButton className={classes.expand} onClick={handleDismiss}>
                        <CloseIcon style={{ color: 'white' }} />
                    </IconButton>
                </div>
            </CardActions>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <Paper className={classes.collapse}>
                    {body}
                    {/* {buttons} */}
                </Paper>
            </Collapse>
        </Card>
    );
});

LogsToExcelSnackbarContent.propTypes = {
    id: PropTypes.number.isRequired,
};

export default LogsToExcelSnackbarContent;